import React, { useState, useEffect, useMemo } from 'react'
import { Col, Row } from 'react-bootstrap'
import { PageTitle } from 'domain/feature/hub/pageTitle/styles'
import { showConfirmationPopUp } from 'components/popup/confirmationModal'
import { showToast } from 'components/toast'
import { postProvider, PostFormat, usePost, DeleteFormat, deleteProvider } from 'sharedKernel/api'
import { v4 as uuidv4 } from 'uuid'
import { Loading } from 'components/loading'
import * as S from './style'
import { InviteForm } from './InviteForm'
import { SearchBar } from './SearchBar'
import { MembersTable } from './MembersTable'
import type { IMember } from './MembersTable'
import { PaginationControls } from './PaginationControls'
import { trans } from 'sharedKernel/i18n'

const FOCAL_POINTS_URL = '/v1/hub/company/public-administration/focal-points'
const FOCAL_POINTS_LIST_URL = `${FOCAL_POINTS_URL}/list`

interface IMembersResponse {
  rows: IMember[]
  totalRows: number
  totalPages: number
  offset: number
  limit: number
}

export const HubPublicAdministrationManagement = (): JSX.Element => {
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [memberName, setMemberName] = useState('')
  const [emailValue, setEmailValue] = useState('')
  const [positionValue, setPositionValue] = useState('')
  const { mutate: addMember } = postProvider()
  const { mutate: resendInvite } = postProvider()

  const membersListPayload = {
    filterBy: [],
    sort: '',
    limit: pageSize,
    page,
    totalRows: 0,
    match: appliedSearch.trim(),
  }

  const membersRequest: PostFormat = {
    url: FOCAL_POINTS_LIST_URL,
    data: membersListPayload,
  }

  const { data: membersData, isFetching, refetch } = usePost<IMembersResponse>(
    membersRequest,
    `focal-points-${page}-${pageSize}-${appliedSearch}`
  )

  const members = useMemo<IMember[]>(() => membersData?.rows ?? [], [membersData])

  const totalPages = membersData?.totalPages ?? 1
  const totalRows = membersData?.totalRows ?? 0

  useEffect(() => {
    setPage(1)
  }, [appliedSearch])

  const handleSearch = (term: string): void => {
    setAppliedSearch(term)
    setSearchInput(term)
  }

  const handleAddMember = (): void => {
    if (!memberName.trim() || !emailValue.trim()) {
      showToast('error', trans('Preencha o nome do membro e o e-mail'))
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(emailValue.trim())) {
      showToast('error', trans('E-mail inválido'))
      return
    }

    const invitePost: PostFormat = {
      url: FOCAL_POINTS_URL,
      data: {
        id: uuidv4(),
        data: {
          name: memberName.trim(),
          email: emailValue.trim(),
          position: positionValue.trim(),
        },
      },
    }

    addMember(invitePost, {
      onSuccess: () => {
        showToast('success', trans('Membro adicionado com sucesso!'))
        setMemberName('')
        setEmailValue('')
        setPositionValue('')
        refetch()
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || error.message || trans('Erro ao adicionar membro')
        showToast('error', errorMessage)
      },
    })
  }

  const handleResendEmail = (member: IMember): void => {
    const resendPost: PostFormat = {
      url: `${FOCAL_POINTS_URL}/${member.id}/resend-invite`,
    }

    resendInvite(resendPost, {
      onSuccess: () => {
        showToast('success', trans('E-mail reenviado com sucesso!'))
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || error.message || trans('Erro ao reenviar e-mail')
        showToast('error', errorMessage)
      },
    })
  }

  const onSuccessDeletion = async (): Promise<void> => {
    showToast('success', trans('Membro removido com sucesso!'))
    refetch()
  }

  const onErrorDeletion = async (error: any): Promise<void> => {
    const errorMessage =
      error.response?.data?.message || error.message || trans('Erro ao remover membro')
    showToast('error', errorMessage)
  }

  const { mutate: removeMember } = deleteProvider(onSuccessDeletion, onErrorDeletion)

  const handleRemove = (member: IMember): void => {
    const request: DeleteFormat = {
      url: `${FOCAL_POINTS_URL}/${member.id}`,
    }

    showConfirmationPopUp({
      title: trans('Remover membro'),
      message: trans('Tem certeza de que deseja remover membro?'),
      onConfirm: () => {
        removeMember(request)
      },
    })
  }

  return (
    <>
      <S.HeaderWrapper>
        <Row className="align-items-center">
          <Col>
            <PageTitle>{trans('Adicionar membros')}</PageTitle>
          </Col>
        </Row>
      </S.HeaderWrapper>

      <InviteForm
        memberName={memberName}
        emailValue={emailValue}
        positionValue={positionValue}
        onChangeName={setMemberName}
        onChangeEmail={setEmailValue}
        onChangePosition={setPositionValue}
        onAdd={handleAddMember}
      />

      <SearchBar
        searchTerm={searchInput}
        onChange={setSearchInput}
        onSearch={handleSearch}
      />

      {isFetching ? (
        <div className="d-flex justify-content-center py-5">
          <Loading />
        </div>
      ) : (
        <MembersTable
          members={members}
          onResendEmail={handleResendEmail}
          onRemove={handleRemove}
        />
      )}

      {!isFetching && (
        <PaginationControls
          page={page}
          totalPages={totalPages}
          totalRows={totalRows}
          onPrev={() => setPage(page - 1)}
          onNext={() => setPage(page + 1)}
        />
      )}
    </>
  )
}
